import React, { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './elementCatalogSlider.css';
import { useShopNames } from '../../commerce/shopNames';

// A row of cards that scrolls sideways a screen at a time, with Sleek's two 48px circle arrows above it —
// what a product row, a collection row, a reviews row and a container of cards all become when they are
// set to slide. When they are not, the track is drawn alone, exactly the box it would have been without this.

/** The track's own attributes: the box its element is styled by, and its column count. */
type TrackAttrs = React.HTMLAttributes<HTMLDivElement> & { className?: string; style?: React.CSSProperties };

/** Where the arrows sit: on their own line above the cards, level with the heading above them, or nowhere. */
export const SLIDER_ARROWS = ['above', 'heading', 'none'] as const;
export type SliderArrows = (typeof SLIDER_ARROWS)[number];

/** What the slider draws: whether it slides, on a phone alone, where its arrows go and what they move, and the cards. */
type CatalogSliderProps = {
  on: boolean;
  editing: boolean;
  track: TrackAttrs;
  children: React.ReactNode;
  phoneOnly?: boolean;
  arrows?: SliderArrows;
};

// Draws the cards in their track, with arrows that move it a screen at a time when the row is a slider.
export const CatalogSlider: React.FC<CatalogSliderProps> = ({ on, editing, track, children, phoneOnly = false, arrows = 'above' }) => {
  const viewport = useRef<HTMLDivElement>(null);
  const names = useShopNames();
  if (!on) return <div {...track}>{children}</div>;
  // Scrolls the row a screen forward or back.
  const step = (by: number) => viewport.current?.scrollBy({ left: by * viewport.current.clientWidth, behavior: 'smooth' });
  // A row that slides on a phone alone is a grid wherever arrows could be clicked, so it is given none.
  const drawsArrows = arrows !== 'none' && !phoneOnly;
  return (
    <div className="xe-slider" data-arrows={drawsArrows ? arrows : undefined}>
      {drawsArrows ? (
        <div className={editing ? 'xs-editor-chrome xe-slider-arrows' : 'xe-slider-arrows'}>
          <button type="button" aria-label={names.previous} onClick={() => step(-1)}><ChevronLeft size={20} aria-hidden="true" /></button>
          <button type="button" aria-label={names.next} onClick={() => step(1)}><ChevronRight size={20} aria-hidden="true" /></button>
        </div>
      ) : null}
      <div ref={viewport} {...track} data-carousel={phoneOnly ? 'phone' : ''}>{children}</div>
    </div>
  );
};
